import { createContext, useContext } from 'react'
import { Alert, Stack, CircularProgress } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { useGetWorkflowState } from '@/service-library/hooks/workflow-states'
import { WorkflowState } from '@/types/workflows'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import WorkflowStateDetails from './WorkflowStateDetails'

type WorkflowStatePageContextValue = ReturnType<typeof useGetWorkflowState> & {
  listQueryKey: QueryKey
  workflowState: WorkflowState
}

export const WorkflowStateContext =
  createContext<WorkflowStatePageContextValue>(
    {} as WorkflowStatePageContextValue,
  )

export const useWorkflowStatePageContext = () =>
  useContext(WorkflowStateContext)

type WorkflowStatePageProps = {
  workflowStateId: string
  workflowState?: WorkflowState
  listQueryKey: QueryKey
}

export default function WorkflowStatePage({
  workflowStateId,
  workflowState,
  listQueryKey,
}: WorkflowStatePageProps) {
  const query = useGetWorkflowState({
    id: workflowStateId,
    filters: {
      fields__include: 'excluded_project_grid_fields_ids,workflow_state_type',
    },
    initialData: workflowState,
    staleTime: 1000,
  })

  const { workflowState: currentState, isPending } = query

  return (
    <>
      {currentState && (
        <ProjectBreadcrumb
          label={currentState?.name || ''}
          url={`../workflow-states/${currentState?.id}`}
        />
      )}

      {!isPending && !currentState && (
        <Alert severity="error">
          Unable to load this workflow state. You may not have access or this
          workflow state does not exist.
        </Alert>
      )}

      {isPending ? (
        <Stack alignItems="center" sx={{ p: 4 }}>
          <CircularProgress />
        </Stack>
      ) : (
        currentState && (
          <WorkflowStateContext.Provider
            value={{
              ...query,
              workflowState: currentState,
              listQueryKey,
            }}
          >
            <WorkflowStateDetails />
          </WorkflowStateContext.Provider>
        )
      )}
    </>
  )
}
